import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { OnboardingLayout } from '../Layout';
import { useAddFamilyMember } from '@/api/queries/family';
import { paths } from '@/routes/paths';
import { useUIStore } from '@/store/ui';
import { isAPIError } from '@/api/error';
import type { FamilyRole, Gender } from '@/api/types';
import { CountryPicker } from '@/components/domain/CountryPicker';
import { BottomSheet } from '@/components/ui/BottomSheet';

const ROLE_VALUES: FamilyRole[] = [
  'parent',
  'spouse',
  'child',
  'brother',
  'sister',
  'father',
  'mother',
  'husband',
  'wife',
  'son',
  'daughter',
  'other',
];

export function OnboardingFamilyFormPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const add = useAddFamilyMember();
  const pushToast = useUIStore((s) => s.pushToast);

  const [given, setGiven] = useState('');
  const [surname, setSurname] = useState('');
  const [dob, setDob] = useState('');
  const [gender, setGender] = useState<Gender | ''>('');
  const [role, setRole] = useState<FamilyRole | ''>('');
  const [nationality, setNationality] = useState('');
  const [passportNumber, setPassportNumber] = useState('');
  const [passportExpiry, setPassportExpiry] = useState('');
  const [issueCountry, setIssueCountry] = useState('');
  const [picker, setPicker] = useState<null | 'nat' | 'issue'>(null);

  const canSubmit = given && surname && role;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !role) return;
    try {
      await add.mutateAsync({
        given_name: given,
        surname,
        date_of_birth: dob || null,
        gender: (gender || null) as Gender | null,
        role,
        nationality_country_code: nationality || null,
        passport_number: passportNumber || null,
        passport_expiry_date: passportExpiry ? `${passportExpiry}-01` : null,
        passport_country_of_issue_code: issueCountry || null,
      });
      navigate(paths.onboardingFamilyList);
    } catch (err) {
      const key = isAPIError(err) ? err.i18nKey : 'errors.generic';
      pushToast({ body: t(key, { defaultValue: t('errors.generic') }), variant: 'error' });
    }
  };

  return (
    <OnboardingLayout back title={t('onboarding.family_form.title')}>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Input label={t('onboarding.profile.given_name')} value={given} onChange={(e) => setGiven(e.target.value)} />
        <Input label={t('onboarding.profile.surname')} value={surname} onChange={(e) => setSurname(e.target.value)} />
        <Input label={t('onboarding.profile.date_of_birth')} type="date" value={dob} onChange={(e) => setDob(e.target.value)} />
        <Select
          label={t('onboarding.profile.gender.label')}
          placeholder="—"
          value={gender}
          onChange={(e) => setGender(e.target.value as Gender)}
          options={[
            { value: 'male', label: t('onboarding.profile.gender.male') },
            { value: 'female', label: t('onboarding.profile.gender.female') },
            { value: 'other', label: t('onboarding.profile.gender.other') },
          ]}
        />
        <Select
          label={t('onboarding.family_form.role.label')}
          placeholder={t('onboarding.family_form.role.placeholder')}
          value={role}
          onChange={(e) => setRole(e.target.value as FamilyRole)}
          options={ROLE_VALUES.map((r) => ({ value: r, label: t(`family_member.role.${r}`) }))}
        />
        <Input
          label={t('onboarding.passport.nationality')}
          value={nationality}
          placeholder="—"
          readOnly
          onClick={() => setPicker('nat')}
        />
        <Input
          label={t('onboarding.passport.passport_number')}
          value={passportNumber}
          onChange={(e) => setPassportNumber(e.target.value.toUpperCase())}
        />
        <Input
          label={t('onboarding.passport.expiry')}
          type="month"
          value={passportExpiry}
          onChange={(e) => setPassportExpiry(e.target.value)}
        />
        <Input
          label={t('onboarding.passport.country_of_issue')}
          value={issueCountry}
          placeholder="—"
          readOnly
          onClick={() => setPicker('issue')}
        />
        <Button type="submit" block size="lg" variant="inverted" disabled={!canSubmit} loading={add.isPending}>
          {t('common.save')}
        </Button>
      </form>

      <BottomSheet open={picker !== null} onClose={() => setPicker(null)} title="—">
        <CountryPicker
          onPick={(code) => {
            if (picker === 'nat') setNationality(code);
            else setIssueCountry(code);
            setPicker(null);
          }}
        />
      </BottomSheet>
    </OnboardingLayout>
  );
}
